import { useCallback, useEffect, useRef, useState } from 'react'
import { moveJob, previewMove } from './machineSequenceApi'
import { classifyMoveError, validatePosition } from './sequencePosition'

// How long the user stops typing before the preview of the written position is asked (S146).
export const PREVIEW_TYPING_PAUSE_MS = 300

const NO_FIELD = { open: false, rowId: 0, position: 0, text: '', openedAt: 0 }
const NO_PREVIEW = { status: 'idle', target: 0, token: '', data: {} }

/**
 * Everything that moves a job in a machine's tab, in one place so the field, the arrows and the menu make the
 * same request for the same target (INV10, RN14, RN15): at most ONE position field open in the tab, the
 * preview asked for what it holds (when the switch is on), the move itself (contract §4) and what follows it.
 * The table's refresh and any other reload wait while a field is open or a move is being saved (D-X, RN35).
 *
 * @param {Object} options
 * @param {Number} options.planId
 * @param {Number} options.deviceId The open machine tab
 * @param {Number} options.jobCount N, the jobs of the machine (Sun's, contract §2)
 * @param {Boolean} options.canOrder The user has "PLANNER_DETAIL_ORDER"
 * @param {Boolean} options.previewOn The resequencing preview switch
 * @param {Function} options.t_
 * @param {Function} options.reloadCalendar Reloads the Planner after a move (raises `planMetricsVersion`)
 * @param {Function} options.setOrderEdited The Planner's "the order was edited" flag (RN17)
 * @param {Function} options.showAlert
 * @param {Function} options.reloadTabs Asks the machine tabs again, at once
 */
const useSequenceMove = ({ planId, deviceId, jobCount, canOrder, previewOn, t_, reloadCalendar, setOrderEdited, showAlert, reloadTabs }) => {
  const [field, setField] = useState(NO_FIELD)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState(NO_PREVIEW)
  // Every request of a preview carries the number it was asked with; only the last one is read (S146, S205).
  const requestRef = useRef(0)
  const typingTimerRef = useRef(0)
  const savingRef = useRef(false)
  const openedCountRef = useRef(0)
  const fieldRef = useRef(field)
  fieldRef.current = field
  const previewRef = useRef(preview)
  previewRef.current = preview
  const latest = useRef({})
  latest.current = { planId, deviceId, jobCount, canOrder, previewOn, t_, reloadCalendar, setOrderEdited, showAlert, reloadTabs }

  const dropPendingPreview = () => {
    requestRef.current += 1
    clearTimeout(typingTimerRef.current)
  }

  const closeField = useCallback(() => {
    if (savingRef.current) return
    dropPendingPreview()
    setField(NO_FIELD)
    setPreview(NO_PREVIEW)
  }, [])

  // The move failed, or the preview did (CU9): say so, close, and ask again for the tabs; the table follows
  // by itself when its refresh resumes.
  const fail = useCallback((error) => {
    const { t_: translate, showAlert: alert, reloadTabs: askTabs } = latest.current
    const { translated, code } = classifyMoveError(error)
    if (translated) alert({ severity: 'warning', title: translate(code) })
    else alert({ severity: 'error', title: translate('The job could not be moved. Nothing has been changed.') })
    dropPendingPreview()
    savingRef.current = false
    setSaving(false)
    setField(NO_FIELD)
    setPreview(NO_PREVIEW)
    askTabs()
  }, [])

  const requestPreview = useCallback(async (rowId, position, target) => {
    const request = requestRef.current + 1
    requestRef.current = request
    setPreview({ ...NO_PREVIEW, status: 'loading', target })
    try {
      const data = await previewMove(latest.current.planId, latest.current.deviceId, { plannerDetailId: rowId, targetPosition: target, expectedCurrentPosition: position })
      if (request !== requestRef.current) return
      if (data === undefined) throw new Error('No answer')
      setPreview({ status: 'ready', target, token: typeof data.sequenceToken === 'string' ? data.sequenceToken : '', data })
    } catch (error) {
      if (request !== requestRef.current) return
      const { translated } = classifyMoveError(error)
      if (translated) fail(error)
      else setPreview({ ...NO_PREVIEW, status: 'error', target })
    }
  }, [fail])

  /** Opens the position field of a row (closing the one open, without saving: S205). */
  const openField = useCallback((row, text = String(row.position)) => {
    // A row Sun sent with no position (`position: null`, contract §7, S209) cannot be moved until the next refresh.
    if (!latest.current.canOrder || savingRef.current || !Number.isInteger(row.position)) return
    dropPendingPreview()
    openedCountRef.current += 1
    setField({ open: true, rowId: row.id, position: row.position, text, openedAt: openedCountRef.current })
    setPreview(NO_PREVIEW)
    const { valid, value } = validatePosition(text, { current: row.position, max: latest.current.jobCount })
    if (latest.current.previewOn && valid) requestPreview(row.id, row.position, value)
  }, [requestPreview])

  const changeText = useCallback((text) => {
    dropPendingPreview()
    setPreview(NO_PREVIEW)
    const { rowId, position } = fieldRef.current
    fieldRef.current = { ...fieldRef.current, text }
    setField((current) => ({ ...current, text }))
    const { valid, value } = validatePosition(text, { current: position, max: latest.current.jobCount })
    if (latest.current.previewOn && valid) {
      typingTimerRef.current = setTimeout(() => requestPreview(rowId, position, value), PREVIEW_TYPING_PAUSE_MS)
    }
  }, [requestPreview])

  const performMove = useCallback(async (rowId, position, target, token) => {
    if (savingRef.current) return
    dropPendingPreview()
    savingRef.current = true
    setSaving(true)
    try {
      const answer = await moveJob(latest.current.planId, latest.current.deviceId, { plannerDetailId: rowId, targetPosition: target, expectedCurrentPosition: position, sequenceToken: token })
      // Api_Functions answers nothing when the connection is lost and the app is sent to its error page.
      if (answer === undefined) throw new Error('No answer')
    } catch (error) {
      fail(error)
      return
    }
    const { reloadCalendar: reload, setOrderEdited: markEdited, reloadTabs: askTabs } = latest.current
    savingRef.current = false
    setField(NO_FIELD)
    setPreview(NO_PREVIEW)
    reload()
    markEdited(true)
    askTabs()
    setSaving(false)
  }, [fail])

  /** Enter, or "Move" in the preview: with a valid value (and, with the switch on, its preview already here). */
  const confirm = useCallback(() => {
    const { open, rowId, position, text } = fieldRef.current
    if (!open || savingRef.current) return
    const { valid, value } = validatePosition(text, { current: position, max: latest.current.jobCount })
    if (!valid) return
    if (!latest.current.previewOn) {
      performMove(rowId, position, value, '')
      return
    }
    const { status, target, token } = previewRef.current
    if (status !== 'ready' || target !== value) return
    performMove(rowId, position, value, token)
  }, [performMove])

  /** An arrow, "Move to the top" or "Move to the bottom": the preview when the switch is on, else the move itself. */
  const moveTo = useCallback((row, target) => {
    if (!latest.current.canOrder || savingRef.current || !Number.isInteger(row.position)) return
    if (latest.current.previewOn) openField(row, String(target))
    else performMove(row.id, row.position, target, '')
  }, [openField, performMove])

  // Another tab or plan: nothing of the old one stays open.
  useEffect(() => {
    dropPendingPreview()
    setField(NO_FIELD)
    setPreview(NO_PREVIEW)
  }, [planId, deviceId])

  // The switch went off with a field open: what was asked is no longer wanted.
  useEffect(() => {
    if (!previewOn) {
      dropPendingPreview()
      setPreview(NO_PREVIEW)
    }
  }, [previewOn])

  useEffect(() => () => clearTimeout(typingTimerRef.current), [])

  return { field, saving, preview, paused: field.open || saving, openField, changeText, closeField, confirm, moveTo }
}

export default useSequenceMove
